import { useMemo } from 'react';
import useCheckoutState, { checkoutStates } from './useCheckoutState';
import { PAGE_TYPES } from '../enums/page-types';
import hasStock from '../lib/hasStock';

const useSubmitDisabled = (paymentElement, paymentElementComplete) => {
  const { state, checkout, product, isUpsell, upsell, isTesting, isTestingPayments, isPreview } =
    useCheckoutState();

  const submitIsDisabled = useMemo(() => {
    const alreadySubmitting =
      state === checkoutStates.CREATING_PAYMENT ||
      state === checkoutStates.CREATING_SUBSCRIPTION ||
      state === checkoutStates.CREATING_SUBMISSION;

    const productIsSoldOut = checkout.type === PAGE_TYPES.CHECKOUT && !hasStock(product);
    const eventIsSoldOut = checkout.type === PAGE_TYPES.EVENT && checkout.eventDetails?.isSoldOut;
    const eventIsCanceled =
      checkout.type === PAGE_TYPES.EVENT && checkout.eventDetails?.status === 'canceled';

    const isTestingUpsellWithoutPayment = isTesting && isUpsell && !upsell;
    const isTestingOutsideTestPayments = isTesting && !isTestingPayments && isPreview;
    const isTestingScenario = isTestingUpsellWithoutPayment || isTestingOutsideTestPayments;

    const paymentElementIsEmpty = paymentElement && !paymentElementComplete;

    if (
      alreadySubmitting ||
      productIsSoldOut ||
      eventIsSoldOut ||
      eventIsCanceled ||
      isTestingScenario ||
      paymentElementIsEmpty
    ) {
      return true;
    }

    return false;
  }, [
    state,
    checkout.type,
    checkout.eventDetails?.soldOut,
    product,
    isUpsell,
    upsell,
    isTesting,
    isTestingPayments,
    isPreview,
    paymentElement,
    paymentElementComplete,
  ]);

  const getDisabledReason = () => {
    const alreadySubmitting =
      state === checkoutStates.CREATING_PAYMENT ||
      state === checkoutStates.CREATING_SUBSCRIPTION ||
      state === checkoutStates.CREATING_SUBMISSION;

    if (alreadySubmitting) return 'submitting';

    const productIsSoldOut = checkout.type === PAGE_TYPES.CHECKOUT && !hasStock(product);
    if (productIsSoldOut) return 'product_sold_out';

    const eventIsSoldOut = checkout.type === PAGE_TYPES.EVENT && checkout.eventDetails?.soldOut;
    if (eventIsSoldOut) return 'event_sold_out';

    const isTestingUpsellWithoutPayment = isTesting && isUpsell && !upsell;
    if (isTestingUpsellWithoutPayment) return 'testing_upsell_without_payment';

    const isTestingOutsideTestPayments = isTesting && !isTestingPayments && isPreview;
    if (isTestingOutsideTestPayments) return 'testing_outside_test_payments';

    const paymentElementIsEmpty = paymentElement && !paymentElementComplete;
    if (paymentElementIsEmpty) return 'payment_element_incomplete';

    return null;
  };

  return {
    submitIsDisabled,
    disabledReason: submitIsDisabled ? getDisabledReason() : null,
  };
};

export default useSubmitDisabled;
